// The bridge from "AI-extracted / hand-built structure" to the exam player's
// EXACT data contract (tests / test_sections / questions / answer_keys). This is
// the verify-green-but-broken gap the advisor flagged: the mapping is invisible
// to typecheck/build, so it is a PURE, heavily-tested function. The same module
// powers Phase 34 (AI classify), Phase 36 (review + publish) and Phase 39
// (manual builder).
//
// MIRRORED to supabase/functions/_shared/ai/extracted-structure.ts
// (byte-identical — no imports).

export const QUESTION_KINDS = [
  'mcq',
  'tfng',
  'ynng',
  'matching_info',
  'matching_headings',
  'matching_features',
  'form_completion',
  'note_completion',
  'table_completion',
  'summary_completion',
  'sentence_completion',
  'short_answer',
] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];

export const SECTION_KINDS = ['listening', 'reading', 'writing', 'speaking'] as const;
export type SectionKind = (typeof SECTION_KINDS)[number];

// Phase 69 — embedded-blank BLOCK kinds. A block owns a shared stimulus +
// option bank; its N blanks become N child question rows (own question number
// + own key). The DB column is free text + check; this is the app-side source.
// Phase 93 adds 'choice_group': an official "Choose TWO/THREE letters" task —
// one shared stem + option list whose N children are the N printed question
// numbers (1 mark each). Its blanks carry NO {{slot}} tokens by design.
export const BLOCK_KINDS = [
  'note',
  'table',
  'flow_chart',
  'diagram',
  'map',
  'summary',
  'matching_box',
  'choice_group',
] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

export interface ParagraphLabel {
  label: string;
  md: string;
}

const AUTO_GRADED_SECTIONS: ReadonlySet<SectionKind> = new Set(['listening', 'reading']);

const KIND_SYNONYMS: ReadonlyArray<readonly [RegExp, QuestionKind]> = [
  [/multiple[\s-]*choice|\bmcq\b/i, 'mcq'],
  [/true[\s/]*false[\s/]*not[\s-]*given|\btfng\b|\bt\/f\/ng\b/i, 'tfng'],
  [/yes[\s/]*no[\s/]*not[\s-]*given|\bynng\b/i, 'ynng'],
  [/matching[\s-]*head/i, 'matching_headings'],
  [/matching[\s-]*feat/i, 'matching_features'],
  [/matching[\s_-]*(info|paragraph|statement)/i, 'matching_info'],
  [/form[\s-]*comp/i, 'form_completion'],
  [/note[\s-]*comp/i, 'note_completion'],
  [/table[\s-]*comp/i, 'table_completion'],
  [/summary[\s-]*comp/i, 'summary_completion'],
  [/sentence[\s-]*comp/i, 'sentence_completion'],
  [/short[\s-]*answer/i, 'short_answer'],
];

/** Map a free-text question-type string (from the model or a human) to a valid
 * enum value, or null when it can't be confidently mapped (→ "needs review"). */
export function mapQuestionKind(raw: string): QuestionKind | null {
  const trimmed = raw.trim().toLowerCase();
  const underscored = trimmed.replace(/[\s-]+/g, '_');
  for (const kind of QUESTION_KINDS) {
    if (kind === underscored) return kind;
  }
  for (const [pattern, kind] of KIND_SYNONYMS) {
    if (pattern.test(trimmed)) return kind;
  }
  if (/\b(completion|gap[\s-]*fill|fill[\s-]*in|blank)\b/i.test(trimmed)) return 'note_completion';
  return null;
}

export interface KeyValidation {
  ok: boolean;
  warning?: string;
}

/** Validate an answer-key DSL pattern at edit time. Catches the constructs the
 * matcher silently mis-handles — notably alternation INSIDE a group, which the
 * grammar does not support (it splits '|' only at the top level). */
export function validateAnswerKey(pattern: string): KeyValidation {
  const trimmed = pattern.trim();
  if (trimmed === '') {
    return { ok: false, warning: 'Empty answer key — nothing will match.' };
  }
  let depth = 0;
  for (const ch of trimmed) {
    if (ch === '(') {
      depth += 1;
    } else if (ch === ')') {
      depth -= 1;
      if (depth < 0) return { ok: false, warning: 'Unbalanced parentheses.' };
    } else if (ch === '|' && depth > 0) {
      return {
        ok: false,
        warning:
          'Alternation “|” inside ( ) isn’t supported. Use top-level alternation, e.g. “a|b”, not “(a|b)”.',
      };
    }
  }
  if (depth !== 0) return { ok: false, warning: 'Unbalanced parentheses.' };
  return { ok: true };
}

// A..H → 0..7. Kept tiny on purpose: option banks never exceed 8 letters.
const LETTER_INDEX: ReadonlyMap<string, number> = new Map(
  ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((ch, i) => [ch, i]),
);

/**
 * MCQ answer keys are matched against the student's SAVED response, which is
 * the full choice TEXT (never a letter). Cambridge keys and AI imports usually
 * supply just "B" — rewrite such keys to accept the actual choice text, keeping
 * the letter as an extra accepted variant. Keys that already equal a choice
 * text (or aren't bare letters) pass through untouched.
 */
export function normalizeChoiceAnswerKey(answerKey: string, choices: readonly string[]): string {
  const trimmed = answerKey.trim();
  const lower = trimmed.toLowerCase();
  if (choices.some((c) => c.trim().toLowerCase() === lower)) return trimmed;
  const idx = LETTER_INDEX.get(lower);
  if (idx === undefined) return trimmed;
  const choice = choices.at(idx)?.trim();
  if (choice === undefined || choice === '') return trimmed;
  // '|' is the DSL's top-level alternation; a choice containing one can't be a
  // safe variant, so keep the letter-only key.
  if (choice.includes('|')) return trimmed;
  return `${choice}|${trimmed}`;
}

/** Same letter→choice-text rewrite for structured multi-select keys
 *  ({ correct: [...] } — the player saves selected choice TEXTS). */
export function normalizeChoiceStructuredAnswer(
  answer: unknown,
  choices: readonly string[],
): unknown {
  if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return answer;
  const record = answer as Record<string, unknown>;
  if (!Array.isArray(record.correct)) return answer;
  const correct = record.correct.map((v) => {
    if (typeof v !== 'string') return v;
    const lower = v.trim().toLowerCase();
    if (choices.some((c) => c.trim().toLowerCase() === lower)) return v;
    const idx = LETTER_INDEX.get(lower);
    const choice = idx === undefined ? undefined : choices.at(idx)?.trim();
    return choice !== undefined && choice !== '' ? choice : v;
  });
  return { ...record, correct };
}

// ---------------------------------------------------------------------------
// Statement-key canonicalization (tfng / ynng)
// ---------------------------------------------------------------------------
//
// The exam player saves TFNG/YNNG responses as the lowercase tokens 'true' /
// 'false' / 'yes' / 'no' / 'not given'. The answer-key DSL matcher compares
// case-insensitively and collapses runs of whitespace, but it never INSERTS a
// space — so a printed key like "NOTGIVEN" (answer sheets often drop the
// space) or a terse "T"/"F"/"NG" could never match any possible student
// response. Canonicalize each top-level alternate onto the kind's token set;
// unrecognized alternates pass through unchanged.

// Slot indices into a kind's token triple: 0 = agrees, 1 = contradicts,
// 2 = not given. Maps keep lookups injection-safe (no variable bracket access).
const STATEMENT_KEY_SLOTS: ReadonlyMap<string, 0 | 1 | 2> = new Map([
  ['true', 0],
  ['t', 0],
  ['yes', 0],
  ['y', 0],
  ['false', 1],
  ['f', 1],
  ['no', 1],
  ['n', 1],
  ['not given', 2],
  ['notgiven', 2],
  ['ng', 2],
]);

const STATEMENT_KEY_TOKENS: ReadonlyMap<QuestionKind, readonly [string, string, string]> = new Map([
  ['tfng', ['TRUE', 'FALSE', 'NOT GIVEN']],
  ['ynng', ['YES', 'NO', 'NOT GIVEN']],
]);

/** Canonicalize a TFNG/YNNG answer key onto the tokens the player can actually
 * save ("NOTGIVEN"/"ng" → "NOT GIVEN", "T" → "TRUE"). Cross-kind synonyms map
 * onto the question's own kind (a sloppy key printing "YES" for a TFNG item
 * becomes "TRUE" — the student can only ever answer TRUE/FALSE/NOT GIVEN).
 * Keys for other kinds — and unrecognized alternates — return unchanged. */
export function canonicalizeStatementKey(kind: QuestionKind | null, key: string): string {
  const tokens = kind === null ? undefined : STATEMENT_KEY_TOKENS.get(kind);
  if (tokens === undefined) return key;
  return key
    .split('|')
    .map((alt) => {
      const norm = alt
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ');
      const slot = STATEMENT_KEY_SLOTS.get(norm) ?? STATEMENT_KEY_SLOTS.get(norm.replace(/ /g, ''));
      const token = slot === undefined ? undefined : tokens.at(slot);
      return token ?? alt.trim();
    })
    .join('|');
}

// ---------------------------------------------------------------------------
// Question-type registry (data-driven types)
// ---------------------------------------------------------------------------
//
// The 12 IELTS types are PINNED here in code and take precedence over any
// passed registry, so their behaviour can never drift. New (PTE-style) types
// come from the `question_types` DB table — callers load the rows and pass a
// registry in. This module stays PURE and import-free; callers build the
// registry via `buildTypeRegistry`.

export type GradingMode = 'auto_exact' | 'auto_partial' | 'ai' | 'manual';

export interface TypeDescriptor {
  code: string;
  gradingMode: GradingMode;
  /** mcq family — the question must carry an explicit choices[] list. */
  requiresChoices: boolean;
}

export type TypeRegistry = ReadonlyMap<string, TypeDescriptor>;

// All 12 IELTS types are auto_exact (DSL key); only mcq carries explicit
// choices. A Map keeps lookups injection-safe (no variable bracket access).
const IELTS_DESCRIPTORS: TypeRegistry = new Map(
  QUESTION_KINDS.map((code): [string, TypeDescriptor] => [
    code,
    { code, gradingMode: 'auto_exact', requiresChoices: code === 'mcq' },
  ]),
);

const GRADING_MODES: ReadonlySet<string> = new Set(['auto_exact', 'auto_partial', 'ai', 'manual']);

/** Build a registry from raw `question_types` rows (DB or test fixtures). */
export function buildTypeRegistry(
  rows: ReadonlyArray<{ code: string; grading_mode: string; input_mode: string }>,
): TypeRegistry {
  const out = new Map<string, TypeDescriptor>();
  for (const r of rows) {
    const gradingMode: GradingMode = GRADING_MODES.has(r.grading_mode)
      ? (r.grading_mode as GradingMode)
      : 'manual';
    out.set(r.code, {
      code: r.code,
      gradingMode,
      requiresChoices: r.input_mode === 'mcq_single' || r.input_mode === 'mcq_multi',
    });
  }
  return out;
}

/** Resolve a code's descriptor. The pinned IELTS map wins, so the 12 built-in
 *  types never drift even if a row for them is also passed in. */
export function descriptorFor(code: string, registry?: TypeRegistry): TypeDescriptor | null {
  return IELTS_DESCRIPTORS.get(code) ?? registry?.get(code) ?? null;
}

/** Where a published answer key for this grading mode is stored. */
export function answerLocationFor(mode: GradingMode): 'key_pattern' | 'answer_jsonb' | 'none' {
  if (mode === 'auto_exact') return 'key_pattern';
  if (mode === 'auto_partial') return 'answer_jsonb';
  return 'none';
}

/** Map a raw type string / explicit code to a registry code, or undefined.
 *  Explicit code wins; then the normalised type string as a code; then the
 *  IELTS synonym map (so an IELTS phrasing resolves to its enum code). */
export function resolveTypeCode(
  rawType: string,
  explicitCode: string | undefined,
  registry?: TypeRegistry,
): string | undefined {
  if (explicitCode && descriptorFor(explicitCode, registry)) return explicitCode;
  const norm = rawType
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (norm && descriptorFor(norm, registry)) return norm;
  return mapQuestionKind(rawType) ?? undefined;
}

// ---------------------------------------------------------------------------
// Editable builder structure
// ---------------------------------------------------------------------------

export interface BuilderQuestion {
  displayOrder: number;
  /** Printed global IELTS number (1..40) when the source carries one. Standalone
   *  questions in a section that ALSO has embedded-blank blocks must keep this so
   *  they interleave with the blocks' numbered blanks instead of restarting at 1
   *  (the blocks reserve their own numbers). Undefined ⇒ number from order. */
  questionNumber?: number | undefined;
  /** 0-based index into the section's passages — links the question to one
   *  passage/part so the player can page per passage. Undefined ⇒ unlinked. */
  passageIndex?: number | undefined;
  /** null when the raw type couldn't be mapped — surfaced as "needs review". */
  kind: QuestionKind | null;
  /** Registry code (new types) — the source of truth. Falls back to `kind`. */
  typeCode?: string | undefined;
  rawKind: string;
  promptMd: string;
  choices: string[];
  answerKey: string;
  /** Teacher-authored "Why?" shown to students AFTER results are published
   *  (Phase 101). Lives beside the key (answer_keys.explanation_md) — never
   *  student-readable before submission. */
  explanationMd?: string | undefined;
  /** Type-specific payload (dropdown options, reorder items, blanks…). */
  payload?: Record<string, unknown> | undefined;
  /** Structured answer key (reorder order, multi-select set, blank picks…). */
  answer?: unknown;
  /** Per-question max points (partial-credit types carry > 1). */
  maxPoints?: number | undefined;
  /** Rubric key for AI-graded types. */
  rubricKey?: string | undefined;
  confirmed: boolean;
}

export interface BuilderPassage {
  displayOrder: number;
  title: string;
  bodyMd: string;
  /** Ordered A–F labels for matching-headings/-info, or undefined. */
  paragraphLabels?: ParagraphLabel[] | undefined;
  /** Listening audio for this passage/part — a path in the private
   *  listening-audio bucket, published as section_passages.audio_path. */
  audioRef?: string | undefined;
  /** Passage figure (graph/chart/illustration printed with the passage) — a
   *  path in the private block-images bucket, published as
   *  section_passages.image_path. PDFs are text-extracted only, so the
   *  teacher attaches this at review (Phase 113). */
  imageRef?: string | undefined;
}

/** One blank inside a block — becomes its own question row (global number + key). */
export interface BuilderBlank {
  /** Printed IELTS question number (1..40), unique within the section. */
  questionNumber: number;
  /** Which {{slot}} token in the block layout this blank fills (1-based). */
  slot: number;
  /** Optional per-blank label/line (else the layout token is the only anchor). */
  promptMd?: string | undefined;
  /** Text/DSL key (auto_exact block types). */
  answerKey?: string | undefined;
  /** Structured key (auto_partial block types — e.g. the chosen option). */
  answer?: unknown;
  /** Teacher-authored "Why?" for this blank (see BuilderQuestion.explanationMd). */
  explanationMd?: string | undefined;
  /** "NO MORE THAN N WORDS" — enforced at render + grade time. */
  wordLimit?: number | undefined;
}

export interface SharedOption {
  label: string;
  text: string;
}

/** A shared-stimulus group: note / table / flow-chart / diagram / map / matching box. */
export interface BuilderBlock {
  displayOrder: number;
  blockKind: BlockKind;
  /** Registry code applied to every child blank (e.g. note_completion). */
  typeCode: string;
  instructionsMd: string;
  /** Stimulus template carrying {{slot}} tokens (note groups / table rows / nodes). */
  layout: Record<string, unknown>;
  /** The A–H option bank shared across all blanks, or undefined. */
  sharedOptions?: SharedOption[] | undefined;
  /** Diagram/map figure reference (resolved to a storage path at publish). */
  imageRef?: string | undefined;
  /** 0-based index into the section's passages (see BuilderQuestion). */
  passageIndex?: number | undefined;
  blanks: BuilderBlank[];
}

export interface BuilderSection {
  kind: SectionKind;
  durationMinutes: number;
  instructionsMd: string;
  /** Listening: ONE continuous recording for the whole section (the real CBT
   *  plays all 4 parts as a single ~30-min stream). A path in the private
   *  listening-audio bucket, published as test_sections.audio_path. Per-part
   *  audio (BuilderPassage.audioRef) remains the alternative. */
  audioRef?: string | undefined;
  /** Duration of the section audio in seconds (read client-side at upload) —
   *  published as test_sections.audio_duration_seconds; review uses it to
   *  suggest durationMinutes = audio + the official 2-minute check. */
  audioDurationSeconds?: number | undefined;
  /** Phase 122 — opt out of play-once for this section: the exam player gains
   *  pause/resume, a seek bar and skip buttons, and writes no play marker.
   *  Published as test_sections.allow_replay. Undefined/false = the real
   *  IELTS behaviour (one play, no rewind); only turn it on for practice. */
  allowReplay?: boolean | undefined;
  passages: BuilderPassage[];
  /** Embedded-blank blocks (note/table/flow-chart/diagram/map/matching box). */
  blocks: BuilderBlock[];
  questions: BuilderQuestion[];
}

export interface BuilderTest {
  title: string;
  examType: 'academic' | 'general';
  sections: BuilderSection[];
}

export interface RawExtractedQuestion {
  questionType: string;
  /** Explicit registry code (universal JSON import sets this). */
  typeCode?: string | undefined;
  /** Printed global IELTS number (1..40) when the source knows it — kept so a
   *  standalone question orders correctly among a section's numbered blocks. */
  questionNumber?: number | undefined;
  /** 0-based index into the section's passages (see BuilderQuestion). */
  passageIndex?: number | undefined;
  promptMd: string;
  choices?: string[] | undefined;
  answerKey?: string | undefined;
  payload?: Record<string, unknown> | undefined;
  answer?: unknown;
  maxPoints?: number | undefined;
  rubricKey?: string | undefined;
}

export interface RawExtractedBlock {
  blockKind: string;
  typeCode: string;
  instructionsMd?: string | undefined;
  layout: Record<string, unknown>;
  sharedOptions?: SharedOption[] | undefined;
  imageRef?: string | undefined;
  /** 0-based index into the section's passages (see BuilderQuestion). */
  passageIndex?: number | undefined;
  blanks: BuilderBlank[];
}

export interface RawExtractedSection {
  kind: string;
  /** Whole-section listening audio (see BuilderSection.audioRef). */
  audioRef?: string | undefined;
  audioDurationSeconds?: number | undefined;
  /** Opt out of play-once (see BuilderSection.allowReplay). */
  allowReplay?: boolean | undefined;
  passages?: {
    title?: string;
    bodyMd?: string;
    paragraphLabels?: ParagraphLabel[] | undefined;
    audioRef?: string | undefined;
    imageRef?: string | undefined;
  }[];
  blocks?: RawExtractedBlock[];
  questions?: RawExtractedQuestion[];
}

const DEFAULT_DURATION = new Map<SectionKind, number>([
  ['listening', 40],
  ['reading', 60],
  ['writing', 60],
  ['speaking', 15],
]);

function sectionDuration(kind: SectionKind): number {
  return DEFAULT_DURATION.get(kind) ?? 60;
}

function asSectionKind(raw: string): SectionKind | null {
  const k = raw.trim().toLowerCase();
  return (SECTION_KINDS as readonly string[]).includes(k) ? (k as SectionKind) : null;
}

function asBlockKind(raw: string): BlockKind {
  const k = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (BLOCK_KINDS as readonly string[]).includes(k) ? (k as BlockKind) : 'note';
}

/** Convert raw model/builder output into the editable structure. Unmappable
 * question types become kind=null (needs review) rather than being dropped. */
export function fromRawSections(
  sections: readonly RawExtractedSection[],
  meta: { title: string; examType: 'academic' | 'general' },
  registry?: TypeRegistry,
): BuilderTest {
  const builderSections: BuilderSection[] = [];
  for (const raw of sections) {
    const kind = asSectionKind(raw.kind);
    if (kind === null) continue;
    const passages: BuilderPassage[] = (raw.passages ?? []).map((p, i) => ({
      displayOrder: i,
      title: p.title ?? '',
      bodyMd: p.bodyMd ?? '',
      paragraphLabels: p.paragraphLabels,
      audioRef: p.audioRef,
      imageRef: p.imageRef,
    }));
    const blocks: BuilderBlock[] = (raw.blocks ?? []).map((b, i) => ({
      displayOrder: i,
      blockKind: asBlockKind(b.blockKind),
      typeCode: b.typeCode,
      instructionsMd: b.instructionsMd ?? '',
      layout: b.layout,
      sharedOptions: b.sharedOptions,
      imageRef: b.imageRef,
      passageIndex: b.passageIndex,
      blanks: b.blanks,
    }));
    const questions: BuilderQuestion[] = (raw.questions ?? []).map((q, i) => {
      const kind = mapQuestionKind(q.questionType);
      return {
        displayOrder: i + 1,
        questionNumber: q.questionNumber,
        passageIndex: q.passageIndex,
        kind,
        typeCode: resolveTypeCode(q.questionType, q.typeCode, registry),
        rawKind: q.questionType,
        promptMd: q.promptMd,
        choices: q.choices ?? [],
        // TFNG/YNNG keys are canonicalized so a printed "NOTGIVEN"/"T" still
        // matches what the player saves; other kinds pass through untouched.
        answerKey: canonicalizeStatementKey(kind, q.answerKey ?? ''),
        payload: q.payload,
        answer: q.answer,
        maxPoints: q.maxPoints,
        rubricKey: q.rubricKey,
        confirmed: false,
      };
    });
    builderSections.push({
      kind,
      durationMinutes: sectionDuration(kind),
      instructionsMd: '',
      audioRef: raw.audioRef,
      audioDurationSeconds: raw.audioDurationSeconds,
      allowReplay: raw.allowReplay,
      passages,
      blocks,
      questions,
    });
  }
  return { title: meta.title, examType: meta.examType, sections: builderSections };
}

// ---------------------------------------------------------------------------
// Validation + materialization
// ---------------------------------------------------------------------------

/** Where a publish finding / import issue points, so the review UI and the
 *  conversational resolve loop can deep-link to the exact item (Phase 106). */
export interface IssueLocation {
  sectionKind?: string;
  /** 1-based passage index. */
  passageNumber?: number;
  /** Printed question number the finding is about. */
  questionNumber?: number;
  /** Inclusive printed-number range for a whole group ([first, last]). */
  questionRange?: [number, number];
  /** A list of printed numbers (missing/duplicate sets). */
  numbers?: number[];
  blockKind?: string;
  typeCode?: string;
}

/** A single located result of validateForPublish. `errors`/`warnings` above are
 *  the flat message projections of this list — both come from the SAME emission
 *  site, so the publish gate and the derived issue list can never disagree on
 *  what blocks (the core Phase-106 invariant). */
export interface PublishFinding {
  severity: 'blocker' | 'warning';
  /** Stable kebab code, e.g. 'passage-count' — drives the issue kind + the
   *  offered resolutions. */
  code: string;
  location: IssueLocation;
  message: string;
}

export interface PublishValidation {
  ok: boolean;
  errors: string[];
  /** Non-blocking advisories (e.g. "39 of the standard 40 items"). */
  warnings: string[];
  /** Every error + warning as a located, coded finding (same emission site). */
  findings: PublishFinding[];
  confirmedCount: number;
  totalCount: number;
}

export interface PublishValidationOptions {
  /** Teacher marked the draft a deliberate partial set — numbering gaps
   *  become warnings instead of publish-blocking errors (Phase 97). */
  allowPartialNumbering?: boolean | undefined;
  /** The draft came from the reading-only Reading Test builder (PDF import).
   *  A full IELTS Reading test always has exactly 3 non-empty passages and all
   *  40 numbered questions — enforce that as a hard publish block so a passage
   *  or question left out of the import can never ship. Manual/JSON/HTML drafts
   *  leave this off and keep the lenient practice-subset rules. */
  requireFullReadingMock?: boolean | undefined;
  /** The draft came from the listening-only Listening Test builder. A full
   *  IELTS Listening test always has exactly 4 parts and all 40 numbered
   *  questions, and is useless without its recording — enforce all three as
   *  hard publish blocks (audio = one whole-section recording OR one per
   *  part). Manual/JSON drafts leave this off. */
  requireFullListeningMock?: boolean | undefined;
  /** The draft came from the manual Speaking builder (ManualSkillBuilder),
   *  which always sets payload.part_index on every row. Enforce that every
   *  row declares a Part 1–3 and Part 2 is exactly one cue card — hard
   *  publish blocks, since a missing/duplicate part shape silently mis-groups
   *  the continuous per-part recording at exam time. Off by default so a
   *  "speaking"-kind section built by something else entirely (e.g. a
   *  registry-driven import/test fixture with no part concept at all) isn't
   *  held to a shape it was never meant to have. */
  requireSpeakingPartShape?: boolean | undefined;
  /** Phase 170 — the draft is a PRACTICE SET with a Listening section. A drill
   *  is as useless without its recording as a full mock is, but it is one part
   *  rather than four, so only the audio rule applies (one whole-section
   *  recording OR one per part). */
  requireListeningAudio?: boolean | undefined;
  /** Phase 170 — how strictly to hold a Speaking section to the Part 1–3
   *  shape. `'full'` is `requireSpeakingPartShape` (every row has a part, Part
   *  2 is exactly one cue card). `'subset'` is a Speaking practice set: every
   *  row still needs a part, but the set may be one cue card alone or Part 1
   *  questions alone, so Part 2 is AT MOST one cue card. Takes precedence over
   *  the boolean when both are given. */
  speakingPartShape?: 'full' | 'subset' | undefined;
}

export interface SectionNumberingReport {
  /** Distinct printed numbers present, ascending. */
  numbers: number[];
  /** Numbers missing strictly inside min..max — an interior gap always means
   *  something was dropped between two numbers that DID survive. */
  gaps: number[];
}

/** The printed-number census of one section, or null when nothing in the
 *  section carries a printed number (legacy/manual drafts stay untouched). */
export function sectionNumberingReport(section: BuilderSection): SectionNumberingReport | null {
  const present = new Set<number>();
  for (const q of section.questions) {
    if (q.questionNumber !== undefined) present.add(q.questionNumber);
  }
  for (const block of section.blocks) {
    for (const blank of block.blanks) present.add(blank.questionNumber);
  }
  if (present.size === 0) return null;
  const numbers = [...present].sort((a, b) => a - b);
  const min = numbers.at(0) ?? 1;
  const max = numbers.at(-1) ?? min;
  const gaps: number[] = [];
  for (let n = min; n <= max; n += 1) {
    if (!present.has(n)) gaps.push(n);
  }
  return { numbers, gaps };
}

/** "12, 13" / "12, 13, … (+4 more)" — bounded list for messages. */
function listNumbers(nums: readonly number[], cap = 10): string {
  const shown = nums.slice(0, cap).join(', ');
  return nums.length > cap ? `${shown} (+${String(nums.length - cap)} more)` : shown;
}

/** The answer letter of a choice_group blank ({ letter: 'B' }), or null. */
function blankAnswerLetter(answer: unknown): string | null {
  if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return null;
  const letter = (answer as Record<string, unknown>).letter;
  return typeof letter === 'string' && letter.trim() !== '' ? letter.trim() : null;
}

/**
 * Option-pool sanity for a block, reused by validateForPublish and the review
 * UI's per-block status chip. For a choice_group: selectCount must equal the
 * number of covered questions and every child needs a DISTINCT answer letter
 * from the option list (that is what makes "choose TWO" grade one mark per
 * printed number with no double-letter exploit). For any other block with a
 * shared option bank: every answer key must resolve to an option label —
 * the exam player saves the LABEL, so a key outside the pool can never match.
 */
export function validatePoolBlock(block: BuilderBlock): string[] {
  const errors: string[] = [];
  const labels = (block.sharedOptions ?? []).map((o) => o.label.trim().toLowerCase());
  if (block.blockKind === 'choice_group') {
    const rawCount = (block.layout as Record<string, unknown>).selectCount;
    const selectCount = typeof rawCount === 'number' ? rawCount : null;
    if (selectCount !== block.blanks.length) {
      errors.push(
        `choose-N: selectCount (${selectCount === null ? 'missing' : String(selectCount)}) ` +
          `must equal its ${String(block.blanks.length)} question number(s).`,
      );
    }
    const seen = new Set<string>();
    for (const blank of block.blanks) {
      const letter = blankAnswerLetter(blank.answer);
      if (letter === null) continue; // "missing answer key" is reported by the generic check
      const lower = letter.toLowerCase();
      if (labels.length > 0 && !labels.includes(lower)) {
        errors.push(
          `Q${String(blank.questionNumber)}: answer “${letter}” is not an option letter.`,
        );
      }
      if (seen.has(lower)) {
        errors.push(`Q${String(blank.questionNumber)}: answer letter “${letter}” is used twice.`);
      }
      seen.add(lower);
    }
    return errors;
  }
  if (labels.length === 0) return errors;
  for (const blank of block.blanks) {
    const key = (blank.answerKey ?? '').trim();
    if (key === '') continue;
    const alternates = key
      .split('|')
      .map((alt) => alt.trim())
      .filter((alt) => alt !== '');
    const anyMatches = alternates.some((alt) => labels.includes(alt.toLowerCase()));
    if (!anyMatches) {
      errors.push(
        `Q${String(blank.questionNumber)}: key “${key}” is not an option label — the player ` +
          `saves the letter, so this could never match. Use the letter (e.g. “${
            (block.sharedOptions ?? [])[0]?.label ?? 'A'
          }”).`,
      );
    }
  }
  return errors;
}

/** IELTS matching-headings/-info instructions declare the passage's paragraph
 *  count, e.g. "Reading Passage 2 has seven paragraphs, A–G". We trust the A–X
 *  letter range (robust to the spelled-out number) to derive the expected count
 *  per passage, and only when the phrase is present — a garbled or absent
 *  instruction simply yields no check, so there is no false positive. */
const DECLARED_PARAGRAPHS_RE =
  /Reading\s+Passage\s+(\d)\s+has\s+\w+\s+(?:paragraphs?|sections?)\s*,?\s*A\s*[–—-]\s*([A-Z])/gi;

function collectDeclaredParagraphCounts(section: BuilderSection): Map<number, number> {
  const out = new Map<number, number>();
  const texts: string[] = [];
  for (const q of section.questions) {
    const gi = q.payload?.['group_instructions_md'];
    if (typeof gi === 'string') texts.push(gi);
  }
  for (const b of section.blocks) texts.push(b.instructionsMd);
  for (const text of texts) {
    for (const m of text.matchAll(DECLARED_PARAGRAPHS_RE)) {
      const passageNumber = Number(m[1]);
      const count = (m[2]?.charCodeAt(0) ?? 0) - 64; // 'A' → 1
      const valid =
        Number.isInteger(passageNumber) && passageNumber >= 1 && count >= 1 && count <= 26;
      if (valid && !out.has(passageNumber)) out.set(passageNumber, count);
    }
  }
  return out;
}

// "[Image #3]" placeholders some re-typeset PDFs print where a figure sat.
const IMAGE_PLACEHOLDER_TEXT_RE = /\[image[ \t]*#?[ \t]*\d*\]/i;

/** Extra figure paths stored in layout.figures (a block can print more than one
 *  — Cambridge 16 Reading Test 4 labels two diagrams under Questions 1–6).
 *  Deliberately a local reader rather than an import of block-layout.ts: this
 *  module is mirrored byte-for-byte into an Edge Function, and block-layout is
 *  not — same reason compactContent above is duplicated. */
function readBlockFigures(block: BuilderBlock): string[] {
  const layout = block.layout as Record<string, unknown>;
  const figures = layout.figures;
  if (!Array.isArray(figures)) return [];
  return figures
    .map((entry) =>
      typeof entry === 'object' && entry !== null
        ? (entry as Record<string, unknown>).imageRef
        : null,
    )
    .filter((ref): ref is string => typeof ref === 'string' && ref !== '');
}

/** Lowercased alphanumerics — content comparison that ignores spacing,
 *  punctuation, and case (same shape as pdf-blueprint's grounding compactor,
 *  duplicated here because this module sits BELOW pdf-blueprint). */
function compactContent(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function validateForPublish(
  test: BuilderTest,
  registry?: TypeRegistry,
  options?: PublishValidationOptions,
): PublishValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const findings: PublishFinding[] = [];
  let confirmedCount = 0;
  let totalCount = 0;
  // errors/warnings are the flat message projections of `findings`; every check
  // pushes through these helpers so the publish gate and the derived issue list
  // stay in lockstep — "no open blocker" is definitionally "gate passes".
  const addError = (code: string, location: IssueLocation, message: string): void => {
    errors.push(message);
    findings.push({ severity: 'blocker', code, location, message });
  };
  const addWarn = (code: string, location: IssueLocation, message: string): void => {
    warnings.push(message);
    findings.push({ severity: 'warning', code, location, message });
  };

  if (test.title.trim() === '') addError('title-missing', {}, 'Test needs a title.');
  if (test.sections.length === 0) addError('no-sections', {}, 'Add at least one section.');

  for (const section of test.sections) {
    const sectionAutoGraded = AUTO_GRADED_SECTIONS.has(section.kind);
    // Reading Test builder hard rule: a full IELTS Reading test always has
    // exactly 3 non-empty passages and all 40 numbered questions. This blocks
    // publishing a draft where the import dropped a passage or a question —
    // scoped to reading-PDF drafts (requireFullReadingMock) so manual practice
    // subsets are unaffected.
    if (options?.requireFullReadingMock === true && section.kind === 'reading') {
      if (section.passages.length !== 3) {
        addError(
          'passage-count',
          { sectionKind: 'reading' },
          `reading: this test has ${String(section.passages.length)} passage(s) — a full IELTS ` +
            `Reading test has exactly 3. Add the missing passage(s) from the source PDF before publishing.`,
        );
      }
      section.passages.forEach((passage, i) => {
        if (passage.bodyMd.trim() === '') {
          addError(
            'passage-empty',
            { sectionKind: 'reading', passageNumber: i + 1 },
            `reading: passage ${String(i + 1)}${
              passage.title.trim() === '' ? '' : ` (“${passage.title.trim()}”)`
            } has no text — paste the passage in from the source PDF before publishing.`,
          );
        }
      });
      const completeness = checkSectionCompleteness(section, 40);
      if (completeness.missingNumbers.length > 0) {
        addError(
          'missing-numbers',
          { sectionKind: 'reading', numbers: completeness.missingNumbers },
          `reading: question number(s) ${listNumbers(completeness.missingNumbers)} are missing — ` +
            `a full IELTS Reading test has all 40. Add the missing question(s) from the source PDF before publishing.`,
        );
      }
    }
    // Listening Test builder hard rule (Phase 120): a full IELTS Listening
    // test always has exactly 4 parts, all 40 numbered questions, and its
    // recording — one whole-section audio (the real CBT's continuous stream)
    // or one per part. Scoped to listening-builder drafts
    // (requireFullListeningMock) so manual practice subsets stay lenient.
    if (options?.requireFullListeningMock === true && section.kind === 'listening') {
      if (section.passages.length !== 4) {
        addError(
          'part-count',
          { sectionKind: 'listening' },
          `listening: this test has ${String(section.passages.length)} part(s) — a full IELTS ` +
            `Listening test has exactly 4. Add the missing part(s) before publishing.`,
        );
      }
      const completeness = checkSectionCompleteness(section, 40);
      if (completeness.missingNumbers.length > 0) {
        addError(
          'missing-numbers',
          { sectionKind: 'listening', numbers: completeness.missingNumbers },
          `listening: question number(s) ${listNumbers(completeness.missingNumbers)} are missing — ` +
            `a full IELTS Listening test has all 40. Add the missing question(s) from the source PDF before publishing.`,
        );
      }
    }
    // The recording rule stands on its own: a full Listening mock needs it
    // (above), and so does a Listening practice set (Phase 170) — one part
    // with no audio is a silent drill, not a lenient one.
    if (
      (options?.requireFullListeningMock === true || options?.requireListeningAudio === true) &&
      section.kind === 'listening'
    ) {
      const sectionAudio = (section.audioRef ?? '').trim() !== '';
      if (!sectionAudio) {
        const silentParts = section.passages
          .map((p, i) => ((p.audioRef ?? '').trim() === '' ? i + 1 : null))
          .filter((n): n is number => n !== null);
        if (section.passages.length === 0 || silentParts.length > 0) {
          addError(
            'audio-missing',
            { sectionKind: 'listening', numbers: silentParts },
            `listening: no recording is attached` +
              (silentParts.length > 0 && silentParts.length < section.passages.length
                ? ` for part(s) ${listNumbers(silentParts)}`
                : '') +
              ` — attach the whole-test audio (or one file per part) before publishing; students ` +
              `cannot sit a silent listening test.`,
          );
        }
      }
    }
    // Phase 109 — reading paragraph-lettering integrity. Matching-headings/-info
    // passages are printed with a contiguous A, B, C… run; a gap, duplicate or
    // non-A start means the extractor dropped or doubled a section marker, which
    // silently mis-letters the passage in the player. And the questions usually
    // DECLARE the count ("Reading Passage 2 has seven paragraphs, A–G") — a
    // mismatch with the detected labels is the classic under-extraction
    // signature (it flags the standfirst-as-A / dropped-paragraph family of
    // import faults at review, before publish).
    if (section.kind === 'reading') {
      section.passages.forEach((passage, i) => {
        const labels = (passage.paragraphLabels ?? []).map((l) => l.label.trim().toUpperCase());
        if (labels.length === 0) return;
        // Only enforce continuity for STANDARD single-letter labels (A, B, C…).
        // Anything exotic (e.g. "Section A") isn't the printed A–G run this check
        // is about, so skip it rather than risk a false publish-block.
        if (!labels.every((l) => /^[A-Z]$/.test(l))) return;
        const expected = Array.from({ length: labels.length }, (_, k) =>
          String.fromCharCode(65 + k),
        );
        if (labels.join(' ') !== expected.join(' ')) {
          const loc: IssueLocation = { sectionKind: 'reading', passageNumber: i + 1 };
          const msg =
            `reading: passage ${String(i + 1)} paragraph letters are ${labels.join(', ')} — they ` +
            `must be a gapless run ${expected.join(', ')} starting at A. Re-extract the passage so ` +
            `the letters match the printed markers.`;
          if (options?.requireFullReadingMock === true) addError('paragraph-labels-gap', loc, msg);
          else addWarn('paragraph-labels-gap', loc, msg);
        }
      });
      for (const [passageNumber, expectedCount] of collectDeclaredParagraphCounts(section)) {
        const passage = section.passages[passageNumber - 1];
        if (passage === undefined) continue;
        const actual = passage.paragraphLabels?.length ?? 0;
        if (actual !== expectedCount) {
          addWarn(
            'paragraph-count-mismatch',
            { sectionKind: 'reading', passageNumber },
            `reading: the questions say passage ${String(passageNumber)} has ` +
              `${String(expectedCount)} lettered paragraph(s), but ${String(actual)} were detected ` +
              `— a paragraph may have been dropped or merged. Re-extract the passage and re-check ` +
              `its matching questions.`,
          );
        }
      }
      // Phase 112 — passage-content hygiene. These are the fingerprints of a
      // pre-hygiene import (or a paste that carried page furniture): a bare
      // letter line duplicates the paragraph badge in the player; a
      // "Questions N–M" line means the question section leaked into the
      // passage; "[Image #N]" is a placeholder the source PDF printed where a
      // figure sat. All warnings — the draft is publishable but looks wrong.
      section.passages.forEach((passage, i) => {
        const loc: IssueLocation = { sectionKind: 'reading', passageNumber: i + 1 };
        const label = `reading: passage ${String(i + 1)}`;
        if (/^[A-H][.)]?$/m.test(passage.bodyMd)) {
          addWarn(
            'passage-letter-line',
            loc,
            `${label} has a paragraph letter on its own line — the player shows letters as ` +
              `badges, so the bare line prints twice. Re-extract the passage or delete the ` +
              `letter line(s) from the text.`,
          );
        }
        if (/^[ \t]*(questions?[ \t]+\d{1,2}\b|you should spend\b)/im.test(passage.bodyMd)) {
          addWarn(
            'passage-question-tail',
            loc,
            `${label} contains a "Questions N–M" heading or the "You should spend…" line — ` +
              `question-section text leaked into the passage. Re-extract the passage or cut ` +
              `everything from that line on.`,
          );
        }
        if (IMAGE_PLACEHOLDER_TEXT_RE.test(passage.bodyMd)) {
          addWarn(
            'passage-image-placeholder',
            loc,
            `${label} contains an "[Image #N]" placeholder printed by the source PDF — ` +
              `delete it or replace it with the figure's caption.`,
          );
        }
      });
    }
    // Phase 137 — speaking part shape. Every row must declare which of the
    // three real IELTS parts it belongs to (payload.part_index) — the exam
    // player groups question rows into one continuous per-part recording by
    // that value, and a missing/out-of-range one doesn't just look wrong, it
    // silently drops the question into the wrong part's recording (or its
    // own stray one) at exam time. Part 2 must be exactly one row: it is
    // always a single cue card, never several questions. The 15/6
    // question-count checks are soft — a practice set intentionally shorter
    // than a real test is fine. Scoped to the manual builder
    // (requireSpeakingPartShape) — see the option's doc for why.
    const speakingShape =
      options?.speakingPartShape ??
      (options?.requireSpeakingPartShape === true ? 'full' : undefined);
    if (speakingShape !== undefined && section.kind === 'speaking') {
      const byPart = new Map<number, number>();
      section.questions.forEach((q, i) => {
        const raw = q.payload?.['part_index'];
        const partIndex = typeof raw === 'number' ? raw : null;
        if (partIndex === null || partIndex < 1 || partIndex > 3) {
          addError(
            'speaking-part-index-invalid',
            { sectionKind: 'speaking', questionNumber: i + 1 },
            `speaking Q${String(i + 1)}: needs a Part number (1–3) — set it in the row's ` +
              `"Part (1–3)" field before publishing.`,
          );
          return;
        }
        byPart.set(partIndex, (byPart.get(partIndex) ?? 0) + 1);
      });
      const part2Count = byPart.get(2) ?? 0;
      // A full mock always has its cue card; a practice set may be Part 1 or
      // Part 3 questions alone — but never two cue cards, which the continuous
      // per-part recorder cannot tell apart.
      if (speakingShape === 'full' ? part2Count !== 1 : part2Count > 1) {
        addError(
          'speaking-part2-not-single',
          { sectionKind: 'speaking' },
          `speaking: Part 2 has ${String(part2Count)} question row(s) — it must have ` +
            (speakingShape === 'full' ? 'exactly one' : 'at most one') +
            ` (the cue card).`,
        );
      }
      const part1Count = byPart.get(1) ?? 0;
      if (part1Count > 15) {
        addWarn(
          'speaking-part1-many-questions',
          { sectionKind: 'speaking' },
          `speaking: Part 1 has ${String(part1Count)} questions — a real test asks up to about 15. ` +
            `Fine for practice; check it's intentional for a full mock.`,
        );
      }
      const part3Count = byPart.get(3) ?? 0;
      if (part3Count > 6) {
        addWarn(
          'speaking-part3-many-questions',
          { sectionKind: 'speaking' },
          `speaking: Part 3 has ${String(part3Count)} questions — a real test asks around 4–6. ` +
            `Fine for practice; check it's intentional for a full mock.`,
        );
      }
    }
    // Phase 97 — numbering completeness gate. A gap INSIDE the numbered range
    // means a printed question was dropped somewhere between import and now;
    // that blocks publish unless the teacher explicitly marked the draft as a
    // partial practice set. Sections that never carried printed numbers
    // (legacy/manual drafts) are exempt — the report is null for them.
    if (sectionAutoGraded) {
      const numbering = sectionNumberingReport(section);
      if (numbering !== null) {
        if (numbering.gaps.length > 0) {
          const msg =
            `${section.kind}: question number(s) ${listNumbers(numbering.gaps)} are missing ` +
            `between ${String(numbering.numbers.at(0) ?? 1)} and ` +
            `${String(numbering.numbers.at(-1) ?? 1)} — a printed paper has no numbering gaps. ` +
            `Add the missing question(s), or mark this draft as a partial practice set.`;
          const loc: IssueLocation = { sectionKind: section.kind, numbers: numbering.gaps };
          if (options?.allowPartialNumbering === true) addWarn('numbering-gap', loc, msg);
          else addError('numbering-gap', loc, msg);
        }
        if (numbering.numbers.length < 40 && numbering.gaps.length === 0) {
          addWarn(
            'under-forty',
            { sectionKind: section.kind },
            `${section.kind}: carries ${String(numbering.numbers.length)} numbered item(s) — ` +
              `a full ${section.kind} paper has 40. Fine for a practice subset; not for a full mock.`,
          );
        }
      }
    }
    // Printed question numbers must be unique within a section — a duplicate
    // (e.g. a typo'd {{N}} gap) silently shifts every on-screen number off the
    // printed paper and grades the section out of one extra item.
    const numberCounts = new Map<number, number>();
    for (const q of section.questions) {
      if (q.questionNumber !== undefined) {
        numberCounts.set(q.questionNumber, (numberCounts.get(q.questionNumber) ?? 0) + 1);
      }
    }
    for (const block of section.blocks) {
      for (const blank of block.blanks) {
        numberCounts.set(blank.questionNumber, (numberCounts.get(blank.questionNumber) ?? 0) + 1);
      }
    }
    for (const [number, count] of numberCounts) {
      if (count > 1) {
        addError(
          'duplicate-number',
          { sectionKind: section.kind, questionNumber: number },
          `${section.kind}: question number ${String(number)} is used ${String(count)} times — every printed number must appear exactly once.`,
        );
      }
    }
    for (const q of section.questions) {
      totalCount += 1;
      if (q.confirmed) confirmedCount += 1;
      const label = `${section.kind} Q${String(q.displayOrder)}`;
      const code = q.typeCode ?? q.kind ?? null;
      const descriptor = code === null ? null : descriptorFor(code, registry);
      const qLoc: IssueLocation = {
        sectionKind: section.kind,
        questionNumber: q.questionNumber ?? q.displayOrder,
        ...(code !== null ? { typeCode: code } : {}),
      };
      if (descriptor === null) {
        addError(
          'question-type-unrecognised',
          qLoc,
          `${label}: unrecognised question type “${q.rawKind}”.`,
        );
      }
      if (q.promptMd.trim() === '') {
        addError('question-prompt-empty', qLoc, `${label}: prompt is empty.`);
      }
      if (descriptor?.requiresChoices && q.choices.filter((c) => c.trim() !== '').length < 2) {
        addError('mcq-too-few-choices', qLoc, `${label}: MCQ needs at least two choices.`);
      }
      // Auto-graded questions in Listening/Reading sections need an answer key.
      // auto_exact → text/DSL key (key_pattern); auto_partial → structured key.
      const isAuto =
        descriptor !== null &&
        (descriptor.gradingMode === 'auto_exact' || descriptor.gradingMode === 'auto_partial');
      if (isAuto && sectionAutoGraded) {
        if (descriptor.gradingMode === 'auto_exact') {
          const keyCheck = validateAnswerKey(q.answerKey);
          if (!keyCheck.ok)
            addError(
              'answer-key-invalid',
              qLoc,
              `${label}: ${keyCheck.warning ?? 'invalid answer key.'}`,
            );
        } else if (q.answer == null) {
          addError('answer-key-missing', qLoc, `${label}: missing answer key.`);
        }
      }
      if (!q.confirmed) {
        addError('question-unconfirmed', qLoc, `${label}: not yet confirmed.`);
      }
      if (
        q.passageIndex !== undefined &&
        (!Number.isInteger(q.passageIndex) ||
          q.passageIndex < 0 ||
          q.passageIndex >= section.passages.length)
      ) {
        addError(
          'question-passage-index',
          qLoc,
          `${label}: passageIndex ${String(q.passageIndex)} but the section has ` +
            `${String(section.passages.length)} passage(s).`,
        );
      }
    }

    for (const block of section.blocks) {
      const blockLabel = `${section.kind} ${block.blockKind} block`;
      const descriptor = descriptorFor(block.typeCode, registry);
      const firstBlankNumber = block.blanks.at(0)?.questionNumber;
      const blockLoc: IssueLocation = {
        sectionKind: section.kind,
        blockKind: block.blockKind,
        typeCode: block.typeCode,
        ...(firstBlankNumber !== undefined ? { questionNumber: firstBlankNumber } : {}),
      };
      if (descriptor === null) {
        addError(
          'block-type-unrecognised',
          blockLoc,
          `${blockLabel}: unrecognised type code “${block.typeCode}”.`,
        );
      }
      if (block.blanks.length === 0) {
        addError('block-empty', blockLoc, `${blockLabel}: needs at least one blank.`);
      }
      if (
        block.passageIndex !== undefined &&
        (!Number.isInteger(block.passageIndex) ||
          block.passageIndex < 0 ||
          block.passageIndex >= section.passages.length)
      ) {
        addError(
          'block-passage-index',
          blockLoc,
          `${blockLabel}: passageIndex ${String(block.passageIndex)} but the section has ` +
            `${String(section.passages.length)} passage(s).`,
        );
      }
      const layoutStr = JSON.stringify(block.layout);
      // Phase 112 — staged-instruction hygiene. A {{N}} token or a printed
      // gap-dot run inside instructionsMd means stimulus text was staged as
      // the banner (pre-hygiene import); a choice_group whose banner contains
      // its own stem prints the stem twice; an extreme banner length is the
      // dumped-window signature. Warnings — re-extraction rebuilds the block.
      const instructionsCompact = compactContent(block.instructionsMd);
      const stemRaw = block.layout.stemMd;
      const stemCompact = typeof stemRaw === 'string' ? compactContent(stemRaw) : '';
      const stemEcho =
        block.blockKind === 'choice_group' &&
        stemCompact.length >= 12 &&
        instructionsCompact.includes(stemCompact);
      if (
        /\{\{\d{1,2}\}\}/.test(block.instructionsMd) ||
        /[.…_]{3,}/.test(block.instructionsMd) ||
        instructionsCompact.length > 600 ||
        stemEcho
      ) {
        addWarn(
          'instructions-contaminated',
          blockLoc,
          `${blockLabel}: the instruction banner carries task content (a gap, the question ` +
            `stem, or a wall of stimulus text) — it should hold only the standard instruction ` +
            `sentences. Re-extract the group to rebuild it.`,
        );
      }
      if (IMAGE_PLACEHOLDER_TEXT_RE.test(layoutStr + block.instructionsMd)) {
        addWarn(
          'block-image-placeholder',
          blockLoc,
          `${blockLabel}: contains an "[Image #N]" placeholder printed by the source PDF — ` +
            `edit it out or attach the real figure to the block.`,
        );
      }
      if (
        (block.blockKind === 'map' || block.blockKind === 'diagram') &&
        (block.imageRef === undefined || block.imageRef === '') &&
        readBlockFigures(block).length === 0
      ) {
        const message =
          `${blockLabel}: this task labels a figure but no image is attached — the student ` +
          `would see the questions with nothing to label. Attach the figure image to the block.`;
        // On a full reading mock the task is simply unanswerable without its
        // figure, so it blocks publish rather than merely warning: the review
        // screen can crop the figure straight out of the source PDF. Practice
        // subsets and manual builds keep the lenient warning.
        if (options?.requireFullReadingMock === true) {
          addError('figure-missing', blockLoc, message);
        } else {
          addWarn('figure-missing', blockLoc, message);
        }
      }
      const seenSlots = new Set<number>();
      const isAutoBlock =
        descriptor !== null &&
        (descriptor.gradingMode === 'auto_exact' || descriptor.gradingMode === 'auto_partial');
      // A choice_group's children ARE the selections — its layout carries no
      // {{slot}} tokens, so the token requirement does not apply to it.
      const requiresSlotTokens = block.blockKind !== 'choice_group';
      for (const blank of block.blanks) {
        totalCount += 1;
        confirmedCount += 1; // block blanks are confirmed as a unit on import.
        const bl = `${blockLabel} Q${String(blank.questionNumber)}`;
        const blankLoc: IssueLocation = {
          sectionKind: section.kind,
          blockKind: block.blockKind,
          typeCode: block.typeCode,
          questionNumber: blank.questionNumber,
        };
        if (seenSlots.has(blank.slot)) {
          addError(
            'blank-duplicate-slot',
            blankLoc,
            `${bl}: duplicate blank slot ${String(blank.slot)}.`,
          );
        }
        seenSlots.add(blank.slot);
        if (requiresSlotTokens && !layoutStr.includes(`{{${String(blank.slot)}}}`)) {
          addError(
            'blank-missing-token',
            blankLoc,
            `${bl}: layout has no {{${String(blank.slot)}}} token.`,
          );
        }
        if (isAutoBlock && sectionAutoGraded && descriptor !== null) {
          if (descriptor.gradingMode === 'auto_exact') {
            const keyCheck = validateAnswerKey(blank.answerKey ?? '');
            if (!keyCheck.ok)
              addError(
                'answer-key-invalid',
                blankLoc,
                `${bl}: ${keyCheck.warning ?? 'invalid answer key.'}`,
              );
          } else if (blank.answer == null) {
            addError('answer-key-missing', blankLoc, `${bl}: missing answer key.`);
          }
        }
      }
      for (const poolError of validatePoolBlock(block)) {
        addError('pool-invalid', blockLoc, `${blockLabel}: ${poolError}`);
      }
    }
  }

  return {
    ok: errors.length === 0 && totalCount > 0,
    errors,
    warnings,
    findings,
    confirmedCount,
    totalCount,
  };
}

export interface SectionCompletenessReport {
  itemCount: number;
  /** Printed numbers 1..expectedTotal with no question/blank carrying them. */
  missingNumbers: number[];
  /** Printed numbers carried by more than one question/blank. */
  duplicateNumbers: number[];
}

/**
 * Deterministic completeness check for an auto-graded (reading/listening)
 * section: does the printed numbering run 1..expectedTotal with no gaps and
 * no duplicates? Independent of AI heading-detection (see pdf-import.ts)
 * so it still catches a partial extraction — e.g. only 1 of 3 passages, or a
 * passage that skipped a few questions — even when that detection didn't
 * fire. IELTS Reading and Listening papers always carry exactly 40 items.
 */
export function checkSectionCompleteness(
  section: BuilderSection,
  expectedTotal = 40,
): SectionCompletenessReport {
  const seen = new Map<number, number>();
  for (const q of section.questions) {
    if (q.questionNumber !== undefined) {
      seen.set(q.questionNumber, (seen.get(q.questionNumber) ?? 0) + 1);
    }
  }
  for (const block of section.blocks) {
    for (const blank of block.blanks) {
      seen.set(blank.questionNumber, (seen.get(blank.questionNumber) ?? 0) + 1);
    }
  }
  const itemCount =
    section.questions.length + section.blocks.reduce((sum, b) => sum + b.blanks.length, 0);
  const missingNumbers: number[] = [];
  for (let n = 1; n <= expectedTotal; n += 1) {
    if (!seen.has(n)) missingNumbers.push(n);
  }
  const duplicateNumbers = [...seen.entries()].filter(([, count]) => count > 1).map(([n]) => n);
  return { itemCount, missingNumbers, duplicateNumbers };
}

export interface OrphanedBlankRescue {
  blockKind: BlockKind;
  questionNumber: number;
  /** 'salvaged' → rebuilt as a typed TFNG/YNNG statement question with its
   *  printed text; 'demoted' → rebuilt as an unconfirmed draft question the
   *  review UI must finish (type/text/key). Nothing is ever dropped. */
  outcome: 'salvaged' | 'demoted';
}

/** Every plain string anywhere inside a block layout (group lines, table
 *  cells, headers, titles) — the places a printed statement line can hide. */
function collectLayoutStrings(node: unknown): string[] {
  const out: string[] = [];
  const visit = (value: unknown): void => {
    if (typeof value === 'string') {
      out.push(value);
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (typeof value === 'object' && value !== null) {
      for (const item of Object.values(value)) visit(item);
    }
  };
  visit(node);
  return out;
}

const TF_KEYS: ReadonlySet<string> = new Set(['t', 'true', 'f', 'false']);
const YN_KEYS: ReadonlySet<string> = new Set(['y', 'yes', 'n', 'no']);
const NG_KEYS: ReadonlySet<string> = new Set(['ng', 'not given', 'notgiven']);

/** Decide whether a block's ORPHANED blanks are really a misfiled statement
 *  group (the classic slip: TFNG items emitted as note-completion blanks with
 *  no gap to token). The blanks' answer keys are the strongest signal
 *  (TRUE/FALSE vs YES/NO); the printed legend in the instructions/layout is
 *  the fallback. Null ⇒ not recognizably a statement group. */
function statementKindFor(
  block: BuilderBlock,
  orphans: readonly BuilderBlank[],
  lines: readonly string[],
): 'tfng' | 'ynng' | null {
  const keys = orphans
    .map((b) =>
      (b.answerKey ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' '),
    )
    .filter((k) => k !== '');
  if (keys.length > 0) {
    if (keys.every((k) => TF_KEYS.has(k) || NG_KEYS.has(k)) && keys.some((k) => TF_KEYS.has(k))) {
      return 'tfng';
    }
    if (keys.every((k) => YN_KEYS.has(k) || NG_KEYS.has(k)) && keys.some((k) => YN_KEYS.has(k))) {
      return 'ynng';
    }
  }
  const hay = `${block.instructionsMd} ${lines.join(' ')}`;
  if (/\btrue\b/i.test(hay) && /\bfalse\b/i.test(hay)) return 'tfng';
  if (/\byes\b/i.test(hay) && /\bno\b/i.test(hay) && /\bnot\s*given\b/i.test(hay)) return 'ynng';
  return null;
}

// A printed statement line: its question number, an optional separator, then
// the statement text ("8   The sisters' childhood influenced …").
const STATEMENT_LINE_RE = /^(\d{1,2})(?!\d)\s*[).:—–-]?\s*(\S.*)$/;

/** Find the printed statement text for a question number among the layout
 *  strings: the line starting with that number plus any unnumbered
 *  continuation lines (PDF text often wraps a statement mid-sentence). */
function statementTextFor(questionNumber: number, lines: readonly string[]): string | null {
  for (const [i, line] of lines.entries()) {
    const match = STATEMENT_LINE_RE.exec(line.trim());
    if (!match || Number(match[1]) !== questionNumber) continue;
    const parts = [match[2] ?? ''];
    for (let j = i + 1; j < lines.length; j += 1) {
      const next = (lines.at(j) ?? '').trim();
      if (next === '' || /^\d{1,2}(?!\d)/.test(next)) break;
      parts.push(next);
    }
    const text = parts.join(' ').trim();
    if (text !== '') return text;
  }
  return null;
}

/**
 * Rescue any block blank whose {{slot}} token isn't actually present in that
 * block's layout text — an AI extraction slip (most often a TRUE/FALSE/NOT
 * GIVEN statement group misfiled as a completion block, which has no gaps to
 * token). There is no review-UI affordance to hand-edit a block's layout
 * text, so left in place an orphaned blank permanently blocks publish — and
 * dropping it (the old behaviour) silently lost the question. Instead every
 * orphan becomes a standalone question the review UI CAN edit: a typed
 * TFNG/YNNG question when the statement group is recognizable ('salvaged'),
 * else an unconfirmed draft flagged for review ('demoted'). Blocks left with
 * no blanks are removed. Returns what was rescued so the caller can warn.
 */
export function salvageOrphanedBlanks(test: BuilderTest): {
  test: BuilderTest;
  rescued: OrphanedBlankRescue[];
} {
  const rescued: OrphanedBlankRescue[] = [];
  const sections: BuilderSection[] = test.sections.map((section) => {
    const extra: BuilderQuestion[] = [];
    let nextOrder = section.questions.reduce((max, q) => Math.max(max, q.displayOrder), 0) + 1;
    const blocks: BuilderBlock[] = [];
    for (const block of section.blocks) {
      // choice_group blanks legitimately have no {{slot}} tokens (the children
      // ARE the selections) — salvaging would demote every choose-N child.
      if (block.blockKind === 'choice_group') {
        blocks.push(block);
        continue;
      }
      const layoutStr = JSON.stringify(block.layout);
      const hasToken = (blank: BuilderBlank): boolean =>
        layoutStr.includes(`{{${String(blank.slot)}}}`);
      const orphans = block.blanks.filter((blank) => !hasToken(blank));
      if (orphans.length === 0) {
        blocks.push(block);
        continue;
      }
      const kept = block.blanks.filter(hasToken);
      const lines = collectLayoutStrings(block.layout);
      const statementKind = statementKindFor(block, orphans, lines);
      for (const blank of orphans) {
        const text = statementTextFor(blank.questionNumber, lines);
        if (statementKind !== null && text !== null) {
          extra.push({
            displayOrder: nextOrder,
            questionNumber: blank.questionNumber,
            passageIndex: block.passageIndex,
            kind: statementKind,
            typeCode: statementKind,
            rawKind: statementKind,
            promptMd: text,
            choices: [],
            answerKey: canonicalizeStatementKey(statementKind, blank.answerKey ?? ''),
            confirmed: false,
          });
          rescued.push({
            blockKind: block.blockKind,
            questionNumber: blank.questionNumber,
            outcome: 'salvaged',
          });
        } else {
          extra.push({
            displayOrder: nextOrder,
            questionNumber: blank.questionNumber,
            passageIndex: block.passageIndex,
            kind: null,
            typeCode: undefined,
            rawKind: block.typeCode,
            promptMd: text ?? blank.promptMd ?? '',
            choices: [],
            answerKey: blank.answerKey ?? '',
            confirmed: false,
          });
          rescued.push({
            blockKind: block.blockKind,
            questionNumber: blank.questionNumber,
            outcome: 'demoted',
          });
        }
        nextOrder += 1;
      }
      if (kept.length > 0) blocks.push({ ...block, blanks: kept });
    }
    if (extra.length === 0 && blocks.length === section.blocks.length) return section;
    return { ...section, blocks, questions: [...section.questions, ...extra] };
  });
  return { test: { ...test, sections }, rescued };
}

export interface MaterializedQuestion {
  displayOrder: number;
  /** Legacy IELTS enum value, or null for registry-only (new) types. */
  kind: QuestionKind | null;
  /** Registry code — the source of truth for the question's type. */
  typeCode: string;
  promptMd: string;
  payload: Record<string, unknown>;
  /** Text/DSL answer key (auto_exact). Empty when the key is structured. */
  answerKey: string;
  /** Structured answer key (auto_partial) — goes to answer_keys.answer_jsonb. */
  answerJsonb?: unknown;
  /** Teacher-authored "Why?" → answer_keys.explanation_md (Phase 101). */
  explanationMd?: string | undefined;
  maxPoints?: number | undefined;
  rubricKey?: string | undefined;
  /** Global IELTS question number (block blanks set this; undefined for legacy). */
  questionNumber?: number | undefined;
  /** Which {{slot}} this blank fills inside its block (block blanks only). */
  blankSlot?: number | undefined;
  /** 0-based index into the (merged) section's passages — publish resolves it
   *  to section_passages.id so the player can page per passage/part. */
  passageIndex?: number | undefined;
}

export interface MaterializedPassage {
  displayOrder: number;
  title: string;
  bodyMd: string;
  paragraphLabels?: ParagraphLabel[] | undefined;
  /** Published as section_passages.audio_path (listening parts). */
  audioRef?: string | undefined;
  /** Published as section_passages.image_path (passage figure). */
  imageRef?: string | undefined;
}

/** A materialized block: the shared stimulus + option bank, carrying its child
 *  blank questions (each with its own global number + key). The publish step
 *  inserts the block, then its child questions with block_id set. */
export interface MaterializedBlock {
  displayOrder: number;
  blockKind: BlockKind;
  instructionsMd: string;
  layout: Record<string, unknown>;
  sharedOptions?: SharedOption[] | undefined;
  imageRef?: string | undefined;
  /** 0-based index into the (merged) section's passages (see MaterializedQuestion). */
  passageIndex?: number | undefined;
  questions: MaterializedQuestion[];
}

export interface MaterializedSection {
  kind: SectionKind;
  displayOrder: number;
  durationMinutes: number;
  instructionsMd: string;
  /** Published as test_sections.audio_path (whole-section listening audio). */
  audioRef?: string | undefined;
  /** Published as test_sections.audio_duration_seconds. */
  audioDurationSeconds?: number | undefined;
  /** Published as test_sections.allow_replay (Phase 122). */
  allowReplay?: boolean | undefined;
  passages: MaterializedPassage[];
  blocks: MaterializedBlock[];
  questions: MaterializedQuestion[];
}

export interface MaterializedTest {
  title: string;
  examType: 'academic' | 'general';
  totalDurationMinutes: number;
  sections: MaterializedSection[];
}

/** Materialize one block: the shared stimulus + each blank as a numbered child
 *  question (1 mark each). Answer routing mirrors materializeTest — auto_exact
 *  keys go to answerKey, auto_partial to answerJsonb. display_order = the global
 *  question number so blanks order naturally in the player. */
function materializeBlock(block: BuilderBlock, registry?: TypeRegistry): MaterializedBlock {
  const descriptor = descriptorFor(block.typeCode, registry);
  const loc = descriptor ? answerLocationFor(descriptor.gradingMode) : 'key_pattern';
  const childKind: QuestionKind | null = (QUESTION_KINDS as readonly string[]).includes(
    block.typeCode,
  )
    ? (block.typeCode as QuestionKind)
    : null;
  const questions: MaterializedQuestion[] = block.blanks.map((blank) => {
    const mq: MaterializedQuestion = {
      displayOrder: blank.questionNumber,
      kind: childKind,
      typeCode: block.typeCode,
      promptMd: blank.promptMd ?? `Blank ${String(blank.slot)}`,
      payload: blank.wordLimit !== undefined ? { word_limit: blank.wordLimit } : {},
      answerKey: loc === 'key_pattern' ? (blank.answerKey ?? '') : '',
      maxPoints: 1,
      questionNumber: blank.questionNumber,
      blankSlot: blank.slot,
    };
    if (loc === 'answer_jsonb' && blank.answer !== undefined) {
      mq.answerJsonb = blank.answer;
    }
    if (blank.explanationMd !== undefined && blank.explanationMd.trim() !== '') {
      mq.explanationMd = blank.explanationMd.trim();
    }
    return mq;
  });
  return {
    displayOrder: block.displayOrder,
    blockKind: block.blockKind,
    instructionsMd: block.instructionsMd,
    layout: block.layout,
    sharedOptions: block.sharedOptions,
    imageRef: block.imageRef,
    questions,
  };
}

/**
 * Flatten a validated BuilderTest into insert-ready rows. Enforces the schema's
 * `unique (test_id, kind)` by MERGING any duplicate-kind sections (their
 * passages + questions are concatenated and re-numbered). MCQ payload matches
 * the player's `{ choices: string[] }` contract verbatim. Call only after
 * validateForPublish() passes. New (registry) types carry a null `kind` and a
 * non-null `typeCode`; their answer key is routed to `answerJsonb` when
 * structured (auto_partial) and to `answerKey` when text/DSL (auto_exact).
 */
export function materializeTest(test: BuilderTest, registry?: TypeRegistry): MaterializedTest {
  const byKind = new Map<SectionKind, MaterializedSection>();
  const order: SectionKind[] = [];

  for (const section of test.sections) {
    let target = byKind.get(section.kind);
    if (!target) {
      target = {
        kind: section.kind,
        displayOrder: order.length,
        durationMinutes: section.durationMinutes,
        instructionsMd: section.instructionsMd,
        passages: [],
        blocks: [],
        questions: [],
      };
      byKind.set(section.kind, target);
      order.push(section.kind);
    }
    // Whole-section audio: the first source section of a kind that carries a
    // recording wins (duplicate-kind sections merge, and two whole-section
    // recordings cannot both play).
    if (target.audioRef === undefined && section.audioRef !== undefined) {
      target.audioRef = section.audioRef;
      target.audioDurationSeconds = section.audioDurationSeconds;
    }
    // Replay is a property of the sitting, not of one recording: if ANY merged
    // source section allows it, the merged section does.
    if (section.allowReplay === true) target.allowReplay = true;
    // Duplicate-kind sections are merged, so this source section's passages land
    // after any already-merged ones — shift its passageIndex links to match.
    const passageOffset = target.passages.length;
    const shiftPassage = (index: number | undefined): number | undefined =>
      index === undefined ? undefined : index + passageOffset;
    for (const p of section.passages) {
      target.passages.push({
        displayOrder: target.passages.length,
        title: p.title,
        bodyMd: p.bodyMd,
        paragraphLabels: p.paragraphLabels,
        audioRef: p.audioRef,
        imageRef: p.imageRef,
      });
    }
    for (const block of section.blocks) {
      const materializedBlock = materializeBlock(block, registry);
      materializedBlock.passageIndex = shiftPassage(block.passageIndex);
      target.blocks.push(materializedBlock);
    }
    for (const q of section.questions) {
      const code = q.typeCode ?? q.kind ?? null;
      if (code === null) continue;
      const descriptor = descriptorFor(code, registry);
      const loc = descriptor ? answerLocationFor(descriptor.gradingMode) : 'key_pattern';
      const choices = q.choices.filter((c) => c.trim() !== '');
      // Order by the question's own global number when it has one (so it slots
      // among the section's numbered blocks); else fall back to a running count.
      const materialized: MaterializedQuestion = {
        displayOrder: q.questionNumber ?? target.questions.length + 1,
        kind: q.kind,
        typeCode: code,
        promptMd: q.promptMd,
        payload: descriptor?.requiresChoices ? { choices } : (q.payload ?? {}),
        answerKey:
          loc === 'key_pattern'
            ? descriptor?.requiresChoices
              ? normalizeChoiceAnswerKey(q.answerKey, choices)
              : q.answerKey
            : '',
        maxPoints: q.maxPoints,
        rubricKey: q.rubricKey,
        questionNumber: q.questionNumber,
        passageIndex: shiftPassage(q.passageIndex),
      };
      if (loc === 'answer_jsonb' && q.answer !== undefined) {
        materialized.answerJsonb = descriptor?.requiresChoices
          ? normalizeChoiceStructuredAnswer(q.answer, choices)
          : q.answer;
      }
      if (q.explanationMd !== undefined && q.explanationMd.trim() !== '') {
        materialized.explanationMd = q.explanationMd.trim();
      }
      target.questions.push(materialized);
    }
  }

  const sections = order.map((kind) => {
    const section = byKind.get(kind);
    if (!section) throw new Error(`materializeTest: missing section ${kind}`);
    return section;
  });
  const totalDurationMinutes = sections.reduce((sum, s) => sum + s.durationMinutes, 0);

  return {
    title: test.title.trim(),
    examType: test.examType,
    totalDurationMinutes,
    sections,
  };
}
